import React, { useState, useContext, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled, { keyframes, css } from 'styled-components';
import '../../styles/page-titles.css';
import { SidebarContext } from "../../AppRoutes";
import PermissionGate from "../../services/PermissionGate";
import { toast } from "react-toastify";
import { FaPix } from "react-icons/fa6";
import {
    FaMoneyBillWave,
    FaCreditCard,
    FaSpinner,
    FaCheckCircle,
    FaTimesCircle,
    FaShieldAlt,
    FaClock,
    FaSyncAlt,
    FaExclamationTriangle,
    FaWallet,
    FaStore,
    FaGlobe
} from "react-icons/fa";
import { MdQrCode2 } from "react-icons/md";

import {
    updateFormasPagamento,
    getEmpresaWithObjId,
    getTunaMerchantStatus,
    updateTunaPaymentMethods,
    syncTunaKycStatus
} from "../../services/api";

import FeatureGate from "../../components/FeatureGate";

// =============================================
// ANIMATIONS
// =============================================

const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const scaleIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;

const pulse = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const slideInRight = keyframes`
    from {
        opacity: 0;
        transform: translateX(20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

// =============================================
// STYLED COMPONENTS
// =============================================

const PageContainer = styled.div`
    display: flex;
    margin-left: ${({ $sidebar }) => ($sidebar ? '250px' : '100px')};
    min-height: 100vh;
    transition: margin-left 200ms ease;
    background: #ffffff;

    @media (max-width: 880px) {
        margin-left: 0;
    }
`;

const ContentWrapper = styled.div`
    width: 100%;
    padding: 24px;

    @media (max-width: 768px) {
        padding: 16px;
    }
`;

/* PageHeader removed - using standardized page-titles.css classes */

const SectionCard = styled.div`
    background: white;
    border-radius: 20px;
    padding: 28px;
    margin-bottom: 24px;
    box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.04),
        0 8px 24px rgba(0, 0, 0, 0.06);
    animation: ${fadeInUp} 0.5s ease-out;
    animation-fill-mode: both;
    border: 1px solid rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.3s ease, transform 0.3s ease;

    &:nth-child(2) {
        animation-delay: 0.1s;
    }

    &:nth-child(3) {
        animation-delay: 0.2s;
    }

    &:hover {
        box-shadow:
            0 4px 12px rgba(0, 0, 0, 0.06),
            0 12px 32px rgba(0, 0, 0, 0.08);
    }

    @media (max-width: 768px) {
        padding: 20px;
        border-radius: 16px;
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 2px solid #f5f5f5;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 60px;
        height: 2px;
        background: ${({ $color }) => $color || 'linear-gradient(135deg, #318cd5, #4facfe)'};
        border-radius: 2px;
    }

    .section-title {
        display: flex;
        align-items: center;
        gap: 14px;

        .icon-box {
            width: 48px;
            height: 48px;
            background: ${({ $color }) => $color || 'linear-gradient(135deg, #318cd5, #4facfe)'};
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
            transition: transform 0.3s ease, box-shadow 0.3s ease;

            &:hover {
                transform: translateY(-2px) scale(1.02);
                box-shadow: 0 6px 16px rgba(0, 0, 0, 0.16);
            }

            svg {
                font-size: 22px;
                color: white;
            }
        }

        .text {
            h2 {
                margin: 0 0 4px 0;
                font-size: 19px;
                font-weight: 600;
                color: #1a1f36;
                letter-spacing: -0.01em;
            }

            p {
                margin: 0;
                font-size: 13px;
                color: #697386;
                letter-spacing: 0.01em;
            }
        }
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`;

const PaymentMethodItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 22px 24px;
    background: ${({ $enabled }) => ($enabled
        ? 'linear-gradient(135deg, #f0f9ff 0%, #e6f4ff 100%)'
        : 'linear-gradient(135deg, #fafbfc 0%, #f5f6f8 100%)')};
    border: 2px solid ${({ $enabled }) => ($enabled ? '#318cd5' : '#eaeef3')};
    border-radius: 16px;
    margin-bottom: 14px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    animation: ${scaleIn} 0.4s ease-out;
    animation-fill-mode: both;
    position: relative;
    overflow: hidden;

    ${({ $enabled }) => $enabled && css`
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(90deg, #318cd5, #4caf50);
        }
    `}

    &:nth-child(1) { animation-delay: 0.05s; }
    &:nth-child(2) { animation-delay: 0.1s; }
    &:nth-child(3) { animation-delay: 0.15s; }
    &:nth-child(4) { animation-delay: 0.2s; }
    &:nth-child(5) { animation-delay: 0.25s; }

    &:last-child {
        margin-bottom: 0;
    }

    &:hover {
        transform: ${({ $disabled }) => $disabled ? 'none' : 'translateY(-2px)'};
        border-color: ${({ $enabled, $disabled }) => $disabled ? '#eaeef3' : ($enabled ? '#2980b9' : '#d0d7e2')};
        box-shadow: ${({ $disabled }) => $disabled ? 'none' : '0 8px 24px rgba(0, 0, 0, 0.08)'};
    }

    .method-info {
        display: flex;
        align-items: center;
        gap: 18px;

        .icon-box {
            width: 56px;
            height: 56px;
            background: ${({ $enabled }) => ($enabled
                ? 'linear-gradient(135deg, #318cd5 0%, #4facfe 100%)'
                : 'linear-gradient(135deg, #d0d7e2 0%, #bfc7d4 100%)')};
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: ${({ $enabled }) => $enabled
                ? '0 4px 12px rgba(49, 140, 213, 0.3)'
                : '0 2px 8px rgba(0, 0, 0, 0.08)'};

            svg {
                font-size: 26px;
                color: white;
                transition: transform 0.3s ease;
            }
        }

        .details {
            h3 {
                margin: 0 0 6px 0;
                font-size: 16px;
                font-weight: 600;
                color: ${({ $enabled }) => $enabled ? '#1a1f36' : '#697386'};
                display: flex;
                align-items: center;
                gap: 10px;
                transition: color 0.3s ease;
            }

            p {
                margin: 0;
                font-size: 13px;
                color: #697386;
                letter-spacing: 0.01em;
            }

            .badge {
                display: inline-flex;
                align-items: center;
                gap: 5px;
                padding: 4px 12px;
                border-radius: 20px;
                font-size: 11px;
                font-weight: 600;
                letter-spacing: 0.02em;
                animation: ${pulse} 2s ease-in-out infinite;

                &.warning {
                    background: linear-gradient(135deg, #fff3cd 0%, #ffe9a0 100%);
                    color: #856404;
                    box-shadow: 0 2px 8px rgba(255, 193, 7, 0.2);
                }

                &.success {
                    background: linear-gradient(135deg, #d4edda 0%, #b8e0c0 100%);
                    color: #155724;
                    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
                }
            }
        }
    }

    .toggle-container {
        display: flex;
        align-items: center;
        gap: 14px;

        .saving-indicator {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            color: #318cd5;
            font-weight: 500;
            animation: ${fadeIn} 0.3s ease-out;
            padding: 6px 12px;
            background: rgba(49, 140, 213, 0.08);
            border-radius: 20px;

            svg {
                animation: ${spin} 0.8s linear infinite;
            }
        }
    }

    opacity: ${({ $disabled }) => $disabled ? 0.55 : 1};
    cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'default'};
    filter: ${({ $disabled }) => $disabled ? 'grayscale(30%)' : 'none'};

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
        padding: 18px;

        .toggle-container {
            width: 100%;
            justify-content: flex-end;
        }
    }
`;

const Toggle = styled.button`
    position: relative;
    width: 60px;
    height: 32px;
    background: ${({ $enabled }) => ($enabled
        ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
        : 'linear-gradient(135deg, #d0d7e2 0%, #bfc7d4 100%)')};
    border: none;
    border-radius: 16px;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: ${({ disabled }) => disabled ? 0.5 : 1};
    box-shadow: ${({ $enabled }) => $enabled
        ? '0 4px 12px rgba(76, 175, 80, 0.35), inset 0 1px 0 rgba(255,255,255,0.2)'
        : '0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255,255,255,0.5)'};

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 50%);
        border-radius: 16px;
        pointer-events: none;
    }

    &::after {
        content: '';
        position: absolute;
        top: 3px;
        left: ${({ $enabled }) => ($enabled ? '31px' : '3px')};
        width: 26px;
        height: 26px;
        background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
        border-radius: 50%;
        transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow:
            0 2px 8px rgba(0, 0, 0, 0.15),
            0 1px 2px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255,255,255,1);
    }

    &:hover:not(:disabled) {
        background: ${({ $enabled }) => ($enabled
            ? 'linear-gradient(135deg, #43a047 0%, #5cb860 100%)'
            : 'linear-gradient(135deg, #c0c8d4 0%, #aeb8c6 100%)')};
        transform: scale(1.02);
    }

    &:active:not(:disabled) {
        transform: scale(0.98);

        &::after {
            width: 30px;
        }
    }
`;

const TunaStatusBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    border-radius: 24px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.02em;
    animation: ${slideInRight} 0.4s ease-out;
    transition: all 0.3s ease;

    background: ${({ $color }) => {
        switch ($color) {
            case 'green': return 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)';
            case 'blue': return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)';
            case 'orange': return 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)';
            case 'gray': return 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)';
            default: return 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)';
        }
    }};
    color: ${({ $color }) => {
        switch ($color) {
            case 'green': return '#2e7d32';
            case 'blue': return '#1565c0';
            case 'orange': return '#e65100';
            case 'gray': return '#616161';
            default: return '#616161';
        }
    }};
    box-shadow: ${({ $color }) => {
        switch ($color) {
            case 'green': return '0 2px 8px rgba(76, 175, 80, 0.2)';
            case 'blue': return '0 2px 8px rgba(33, 150, 243, 0.2)';
            case 'orange': return '0 2px 8px rgba(255, 152, 0, 0.2)';
            default: return '0 2px 8px rgba(0, 0, 0, 0.08)';
        }
    }};

    svg {
        font-size: 14px;
    }

    &:hover {
        transform: translateY(-1px);
    }
`;

const KycStatusGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 18px;
    margin-bottom: 24px;
`;

const KycStatusItem = styled.div`
    background: ${({ $color }) => {
        switch ($color) {
            case 'green': return 'linear-gradient(145deg, #e8f5e9 0%, #c8e6c9 50%, #a5d6a7 100%)';
            case 'yellow': return 'linear-gradient(145deg, #fff8e1 0%, #ffecb3 50%, #ffe082 100%)';
            case 'red': return 'linear-gradient(145deg, #ffebee 0%, #ffcdd2 50%, #ef9a9a 100%)';
            case 'blue': return 'linear-gradient(145deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%)';
            default: return 'linear-gradient(145deg, #f5f5f5 0%, #e8e8e8 50%, #d0d0d0 100%)';
        }
    }};
    border-left: 5px solid ${({ $color }) => {
        switch ($color) {
            case 'green': return '#4caf50';
            case 'yellow': return '#ff9800';
            case 'red': return '#f44336';
            case 'blue': return '#2196f3';
            default: return '#9e9e9e';
        }
    }};
    border-radius: 14px;
    padding: 20px;
    animation: ${scaleIn} 0.4s ease-out;
    animation-fill-mode: both;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    &:nth-child(1) { animation-delay: 0.1s; }
    &:nth-child(2) { animation-delay: 0.2s; }

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
    }

    .kyc-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;

        .kyc-title {
            display: flex;
            align-items: center;
            gap: 12px;

            .icon-box {
                width: 40px;
                height: 40px;
                border-radius: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: ${({ $color }) => {
                    switch ($color) {
                        case 'green': return 'linear-gradient(135deg, #4caf50, #66bb6a)';
                        case 'yellow': return 'linear-gradient(135deg, #ff9800, #ffb74d)';
                        case 'red': return 'linear-gradient(135deg, #f44336, #e57373)';
                        case 'blue': return 'linear-gradient(135deg, #2196f3, #64b5f6)';
                        default: return 'linear-gradient(135deg, #9e9e9e, #bdbdbd)';
                    }
                }};
                box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);

                svg {
                    color: white;
                    font-size: 18px;
                }
            }

            h4 {
                margin: 0;
                font-size: 16px;
                font-weight: 600;
                color: #1a1f36;
            }
        }
    }

    .kyc-badge {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 5px 12px;
        border-radius: 16px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        background: ${({ $color }) => {
            switch ($color) {
                case 'green': return 'linear-gradient(135deg, #4caf50, #66bb6a)';
                case 'yellow': return 'linear-gradient(135deg, #ff9800, #ffb74d)';
                case 'red': return 'linear-gradient(135deg, #f44336, #e57373)';
                case 'blue': return 'linear-gradient(135deg, #2196f3, #64b5f6)';
                default: return 'linear-gradient(135deg, #9e9e9e, #bdbdbd)';
            }
        }};
        color: white;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }

    .kyc-description {
        font-size: 13px;
        color: #555;
        margin: 0;
        line-height: 1.5;
    }
`;

const RefreshButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background: transparent;
    border: 2px solid #318cd5;
    color: #318cd5;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(49, 140, 213, 0.1), transparent);
        transition: left 0.5s ease;
    }

    &:hover:not(:disabled) {
        background: linear-gradient(135deg, #318cd5, #4facfe);
        color: white;
        border-color: transparent;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(49, 140, 213, 0.3);

        &::before {
            left: 100%;
        }
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .spinner {
        animation: ${spin} 0.8s linear infinite;
    }
`;

const WarningBox = styled.div`
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 50%, #ffcc80 100%);
    border-left: 5px solid #ff9800;
    border-radius: 14px;
    padding: 18px 22px;
    margin-top: 20px;
    animation: ${fadeInUp} 0.4s ease-out;
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.15);

    .warning-content {
        display: flex;
        align-items: flex-start;
        gap: 14px;

        svg {
            font-size: 22px;
            color: #e65100;
            flex-shrink: 0;
            margin-top: 2px;
            animation: ${pulse} 2s ease-in-out infinite;
        }

        .text {
            h4 {
                margin: 0 0 6px 0;
                color: #e65100;
                font-size: 15px;
                font-weight: 600;
            }

            p {
                margin: 0;
                color: #bf360c;
                font-size: 13px;
                line-height: 1.5;
            }
        }
    }
`;

const TunaNotActiveBox = styled.div`
    background: linear-gradient(145deg, #f8f9fa 0%, #e9ecef 50%, #dee2e6 100%);
    border-radius: 18px;
    padding: 40px 32px;
    text-align: center;
    animation: ${scaleIn} 0.5s ease-out;
    border: 2px dashed #d0d7e2;

    .icon-large {
        width: 80px;
        height: 80px;
        background: linear-gradient(135deg, #adb5bd, #868e96);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 20px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        transition: transform 0.3s ease;

        &:hover {
            transform: scale(1.05) rotate(-5deg);
        }

        svg {
            font-size: 36px;
            color: white;
        }
    }

    h3 {
        margin: 0 0 10px 0;
        font-size: 20px;
        font-weight: 600;
        color: #343a40;
    }

    p {
        margin: 0 0 24px 0;
        color: #6c757d;
        font-size: 14px;
        max-width: 400px;
        margin-left: auto;
        margin-right: auto;
        line-height: 1.6;
    }

    a {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        padding: 14px 28px;
        background: linear-gradient(135deg, #318cd5 0%, #4facfe 100%);
        color: white;
        text-decoration: none;
        border-radius: 12px;
        font-weight: 600;
        font-size: 15px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 4px 16px rgba(49, 140, 213, 0.3);

        &:hover {
            transform: translateY(-3px);
            box-shadow: 0 8px 24px rgba(49, 140, 213, 0.4);
        }

        &:active {
            transform: translateY(-1px);
        }
    }
`;

const LoadingOverlay = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px;
    color: #697386;
    animation: ${fadeIn} 0.3s ease-out;

    svg {
        font-size: 44px;
        margin-bottom: 18px;
        animation: ${spin} 0.9s linear infinite;
        color: #318cd5;
    }

    span {
        font-size: 15px;
        font-weight: 500;
    }
`;

const KycSectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding: 12px 16px;
    background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
    border-radius: 12px;
    animation: ${fadeIn} 0.4s ease-out;

    .kyc-label {
        display: flex;
        align-items: center;
        gap: 10px;
        color: #495057;
        font-size: 14px;
        font-weight: 600;

        svg {
            color: #318cd5;
            font-size: 18px;
        }
    }
`;

const EmptyKycState = styled.div`
    text-align: center;
    padding: 24px 16px;
    color: #adb5bd;
    font-size: 13px;
    background: #f8f9fa;
    border-radius: 12px;
    border: 2px dashed #e9ecef;
`;

// =============================================
// COMPONENT
// =============================================

const FormasPagamento = () => {
    const { t } = useTranslation('paymentMethods');
    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);
    const empresaObjId = empresaParse._id;

    const { sidebar } = useContext(SidebarContext);

    // Estado para pagamentos presenciais
    const [formasPagamento, setFormasPagamento] = useState([]);
    const [loadingPresenciais, setLoadingPresenciais] = useState(true);
    const [savingMethod, setSavingMethod] = useState(null);

    // Estado para Tuna
    const [tunaActive, setTunaActive] = useState(false);
    const [tunaPending, setTunaPending] = useState(false);
    const [loadingTuna, setLoadingTuna] = useState(true);
    const [tunaPaymentMethods, setTunaPaymentMethods] = useState({
        pix: false,
        credit_card: false
    });
    const [kycStatus, setKycStatus] = useState(null);
    const [loadingKyc, setLoadingKyc] = useState(false);

    // Helper para mapear kycLevel
    const getKycLevelInfo = (kycLevel, isPix = false) => {
        const map = {
            'R': {
                code: 'R',
                label: t('kyc.levels.rejected.label'),
                description: t('kyc.levels.rejected.description'),
                color: 'red'
            },
            '1': {
                code: '1',
                label: t('kyc.levels.approved.label'),
                description: t('kyc.levels.approved.description'),
                color: 'green'
            },
            'A': {
                code: 'A',
                label: t('kyc.levels.approvedAlert.label'),
                description: t('kyc.levels.approvedAlert.description'),
                color: 'yellow'
            },
            'P': {
                code: 'P',
                label: t('kyc.levels.approvedPep.label'),
                description: t('kyc.levels.approvedPep.description'),
                color: 'yellow'
            },
            '0': {
                code: '0',
                label: isPix ? t('kyc.levels.available.label') : t('kyc.levels.analyzing.label'),
                description: isPix
                    ? t('kyc.levels.available.description')
                    : t('kyc.levels.analyzing.description'),
                color: isPix ? 'green' : 'blue'
            }
        };
        return map[kycLevel] || {
            code: null,
            label: t('kyc.levels.pendingSend.label'),
            description: t('kyc.levels.pendingSend.description'),
            color: 'gray'
        };
    };

    // Carregar dados da empresa (formas de pagamento presenciais)
    useEffect(() => {
        const fetchEmpresa = async () => {
            try {
                setLoadingPresenciais(true);
                const empresaTemp = await getEmpresaWithObjId(empresaObjId);
                setFormasPagamento(empresaTemp.data.empresa.formas_pagamento || []);
            } catch (error) {
                console.error("Erro ao buscar empresa:", error);
                toast.error(t('toasts.loadError'));
            } finally {
                setLoadingPresenciais(false);
            }
        };

        fetchEmpresa();
    }, [empresaObjId, t]);

    // Carregar status do Tuna
    const fetchTunaStatus = useCallback(async () => {
        try {
            setLoadingTuna(true);
            const response = await getTunaMerchantStatus();

            if (response.success && response.merchant) {
                const status = response.merchant.merchant_status || response.merchant.status;
                setTunaActive(['active', 'pending'].includes(status));
                setTunaPending(status === 'pending');

                if (response.merchant.payment_methods_enabled) {
                    setTunaPaymentMethods(response.merchant.payment_methods_enabled);
                }

                // Buscar KYC silenciosamente
                handleSyncKycStatus(true);
            } else {
                setTunaActive(false);
            }
        } catch (error) {
            console.error("Erro ao buscar status Tuna:", error);
            setTunaActive(false);
        } finally {
            setLoadingTuna(false);
        }
    }, []);

    useEffect(() => {
        fetchTunaStatus();
    }, [fetchTunaStatus]);

    // Sincronizar KYC
    const handleSyncKycStatus = async (silent = false) => {
        try {
            setLoadingKyc(true);
            if (!silent) {
                toast.info(t('toasts.syncingTuna'));
            }

            const response = await syncTunaKycStatus();

            if (response.success) {
                setKycStatus({
                    pix: {
                        is_approved: response.pix_kyc?.is_approved || false,
                        can_transact: response.pix_kyc?.kyc_level !== 'R',
                        kyc_level: getKycLevelInfo(response.pix_kyc?.kyc_level, true)
                    },
                    card: {
                        is_approved: response.card_kyc?.is_approved || false,
                        can_enable: response.card_kyc?.is_approved || false,
                        kyc_level: getKycLevelInfo(response.card_kyc?.kyc_level, false)
                    }
                });

                if (response.payment_methods_enabled) {
                    setTunaPaymentMethods(response.payment_methods_enabled);
                }

                if (!silent) {
                    toast.success(t('toasts.kycUpdated'));
                }
            }
        } catch (error) {
            console.error("Erro ao sincronizar KYC:", error);
            if (!silent) {
                toast.error(t('toasts.syncError'));
            }
        } finally {
            setLoadingKyc(false);
        }
    };

    // Toggle pagamento presencial (salva automaticamente)
    const handleTogglePresencial = async (forma) => {
        const wasEnabled = formasPagamento.includes(forma);
        const newFormas = wasEnabled
            ? formasPagamento.filter(item => item !== forma)
            : [...formasPagamento, forma];

        // Atualiza UI imediatamente
        setFormasPagamento(newFormas);
        setSavingMethod(forma);

        // Get method name for toast
        const methodNames = {
            'Pix': t('methods.pix.title'),
            'Dinheiro': t('methods.cash.title'),
            'Cartão': t('methods.card.title')
        };
        const methodName = methodNames[forma] || forma;

        try {
            await updateFormasPagamento(empresaObjId, newFormas);
            toast.success(wasEnabled
                ? t('toasts.methodDisabled', { method: methodName })
                : t('toasts.methodEnabled', { method: methodName })
            );
        } catch (error) {
            console.error("Erro ao salvar:", error);
            toast.error(t('toasts.updateError'));
            // Reverte em caso de erro
            setFormasPagamento(wasEnabled ? [...formasPagamento] : formasPagamento.filter(item => item !== forma));
        } finally {
            setSavingMethod(null);
        }
    };

    // Toggle pagamento Tuna (salva automaticamente)
    const handleToggleTuna = async (method) => {
        // Verificar KYC para cartao
        if (method === 'credit_card' && !tunaPaymentMethods.credit_card) {
            if (!kycStatus?.card?.can_enable) {
                toast.warning(t('toasts.cardKycRequired'));
                return;
            }
        }

        const newMethods = {
            ...tunaPaymentMethods,
            [method]: !tunaPaymentMethods[method]
        };

        // Garantir pelo menos um metodo
        if (!newMethods.pix && !newMethods.credit_card) {
            toast.warning(t('toasts.atLeastOneMethod'));
            return;
        }

        // Atualiza UI imediatamente
        setTunaPaymentMethods(newMethods);
        setSavingMethod(method === 'pix' ? 'tuna_pix' : 'tuna_card');

        const methodName = method === 'pix' ? t('methods.pixOnline.title') : t('methods.creditCardOnline.title');

        try {
            const response = await updateTunaPaymentMethods(newMethods);
            if (response.success) {
                toast.success(newMethods[method]
                    ? t('toasts.methodEnabled', { method: methodName })
                    : t('toasts.methodDisabled', { method: methodName })
                );
            } else {
                throw new Error(response.error);
            }
        } catch (error) {
            console.error("Erro ao atualizar Tuna:", error);
            toast.error(t('toasts.updateError'));
            // Reverte
            setTunaPaymentMethods({
                ...tunaPaymentMethods,
                [method]: !newMethods[method]
            });
        } finally {
            setSavingMethod(null);
        }
    };

    const presenciaisLoading = loadingPresenciais;
    const isLoading = presenciaisLoading && loadingTuna;

    if (isLoading) {
        return (
            <PermissionGate permissions={['default']}>
                <PageContainer $sidebar={sidebar}>
                    <ContentWrapper>
                        <LoadingOverlay>
                            <FaSpinner />
                            <span>{t('loading.config')}</span>
                        </LoadingOverlay>
                    </ContentWrapper>
                </PageContainer>
            </PermissionGate>
        );
    }

    return (
        <PermissionGate permissions={['default']}>
            <PageContainer $sidebar={sidebar}>
                <ContentWrapper>
                    {/* Header */}
                    <div className="page-title-with-subtitle">
                        <div className="page-title-with-icon">
                            <div className="page-icon"><FaWallet /></div>
                            <h1 className="page-title">{t('page.title')}</h1>
                        </div>
                        <p className="page-subtitle">{t('page.subtitle')}</p>
                    </div>

                    {/* Secao: Pagamentos Presenciais */}
                    <SectionCard>
                        <SectionHeader $color="linear-gradient(135deg, #4caf50, #66bb6a)">
                            <div className="section-title">
                                <div className="icon-box">
                                    <FaStore />
                                </div>
                                <div className="text">
                                    <h2>{t('sections.presencial.title')}</h2>
                                    <p>{t('sections.presencial.subtitle')}</p>
                                </div>
                            </div>
                        </SectionHeader>

                        {loadingPresenciais ? (
                            <LoadingOverlay style={{ padding: '40px' }}>
                                <FaSpinner style={{ fontSize: '28px' }} />
                                <span>{t('loading.general')}</span>
                            </LoadingOverlay>
                        ) : (
                            <>
                                {/* PIX Presencial - only shown when pix feature is enabled */}
                                <FeatureGate feature="pix">
                                <PaymentMethodItem $enabled={formasPagamento.includes('Pix')}>
                                    <div className="method-info">
                                        <div className="icon-box">
                                            <FaPix />
                                        </div>
                                        <div className="details">
                                            <h3>{t('methods.pix.title')}</h3>
                                            <p>{t('methods.pix.description')}</p>
                                        </div>
                                    </div>
                                    <div className="toggle-container">
                                        {savingMethod === 'Pix' && (
                                            <div className="saving-indicator">
                                                <FaSpinner /> {t('loading.saving')}
                                            </div>
                                        )}
                                        <Toggle
                                            $enabled={formasPagamento.includes('Pix')}
                                            onClick={() => handleTogglePresencial('Pix')}
                                            disabled={savingMethod === 'Pix'}
                                        />
                                    </div>
                                </PaymentMethodItem>
                                </FeatureGate>

                                {/* Dinheiro */}
                                <PaymentMethodItem $enabled={formasPagamento.includes('Dinheiro')}>
                                    <div className="method-info">
                                        <div className="icon-box">
                                            <FaMoneyBillWave />
                                        </div>
                                        <div className="details">
                                            <h3>{t('methods.cash.title')}</h3>
                                            <p>{t('methods.cash.description')}</p>
                                        </div>
                                    </div>
                                    <div className="toggle-container">
                                        {savingMethod === 'Dinheiro' && (
                                            <div className="saving-indicator">
                                                <FaSpinner /> {t('loading.saving')}
                                            </div>
                                        )}
                                        <Toggle
                                            $enabled={formasPagamento.includes('Dinheiro')}
                                            onClick={() => handleTogglePresencial('Dinheiro')}
                                            disabled={savingMethod === 'Dinheiro'}
                                        />
                                    </div>
                                </PaymentMethodItem>

                                {/* Cartao Presencial */}
                                <PaymentMethodItem $enabled={formasPagamento.includes('Cartão')}>
                                    <div className="method-info">
                                        <div className="icon-box">
                                            <FaCreditCard />
                                        </div>
                                        <div className="details">
                                            <h3>{t('methods.card.title')}</h3>
                                            <p>{t('methods.card.description')}</p>
                                        </div>
                                    </div>
                                    <div className="toggle-container">
                                        {savingMethod === 'Cartão' && (
                                            <div className="saving-indicator">
                                                <FaSpinner /> {t('loading.saving')}
                                            </div>
                                        )}
                                        <Toggle
                                            $enabled={formasPagamento.includes('Cartão')}
                                            onClick={() => handleTogglePresencial('Cartão')}
                                            disabled={savingMethod === 'Cartão'}
                                        />
                                    </div>
                                </PaymentMethodItem>
                            </>
                        )}
                    </SectionCard>

                    {/* Secao: Pagamentos Online (Tuna) - only shown when tunaPayments feature is enabled */}
                    <FeatureGate feature="tunaPayments">
                    <SectionCard>
                        <SectionHeader $color="linear-gradient(135deg, #318cd5, #4facfe)">
                            <div className="section-title">
                                <div className="icon-box">
                                    <FaGlobe />
                                </div>
                                <div className="text">
                                    <h2>{t('sections.online.title')}</h2>
                                    <p>{t('sections.online.subtitle')}</p>
                                </div>
                            </div>
                            {tunaActive && (
                                <TunaStatusBadge $color={tunaPending ? 'blue' : 'green'}>
                                    {tunaPending ? <FaClock /> : <FaCheckCircle />}
                                    {tunaPending ? t('status.pending') : t('status.active')}
                                </TunaStatusBadge>
                            )}
                        </SectionHeader>

                        {loadingTuna ? (
                            <LoadingOverlay style={{ padding: '40px' }}>
                                <FaSpinner style={{ fontSize: '28px' }} />
                                <span>{t('loading.tuna')}</span>
                            </LoadingOverlay>
                        ) : !tunaActive ? (
                            <TunaNotActiveBox>
                                <div className="icon-large">
                                    <FaCreditCard />
                                </div>
                                <h3>{t('tunaNotActive.title')}</h3>
                                <p>{t('tunaNotActive.description')}</p>
                                <a href="/configuracao-tuna">
                                    <FaCreditCard />
                                    {t('tunaNotActive.activateButton')}
                                </a>
                            </TunaNotActiveBox>
                        ) : (
                            <>
                                {/* Status KYC */}
                                <div style={{ marginBottom: '24px' }}>
                                    <KycSectionHeader>
                                        <div className="kyc-label">
                                            <FaShieldAlt />
                                            <span>{t('kyc.title')}</span>
                                        </div>
                                        <RefreshButton onClick={() => handleSyncKycStatus(false)} disabled={loadingKyc}>
                                            {loadingKyc ? <FaSpinner className="spinner" /> : <FaSyncAlt />}
                                            {t('kyc.syncButton')}
                                        </RefreshButton>
                                    </KycSectionHeader>

                                    {loadingKyc ? (
                                        <LoadingOverlay style={{ padding: '24px' }}>
                                            <FaSpinner style={{ fontSize: '24px' }} />
                                            <span>{t('kyc.loading')}</span>
                                        </LoadingOverlay>
                                    ) : kycStatus ? (
                                        <KycStatusGrid>
                                            <KycStatusItem $color={kycStatus.pix?.kyc_level?.color || 'gray'}>
                                                <div className="kyc-header">
                                                    <div className="kyc-title">
                                                        <div className="icon-box">
                                                            <MdQrCode2 />
                                                        </div>
                                                        <h4>{t('kyc.pixLabel')}</h4>
                                                    </div>
                                                    <span className="kyc-badge">
                                                        {kycStatus.pix?.can_transact ? (
                                                            <><FaCheckCircle /> {kycStatus.pix?.kyc_level?.label}</>
                                                        ) : (
                                                            <><FaTimesCircle /> {kycStatus.pix?.kyc_level?.label}</>
                                                        )}
                                                    </span>
                                                </div>
                                                <p className="kyc-description">{kycStatus.pix?.kyc_level?.description}</p>
                                            </KycStatusItem>

                                            <KycStatusItem $color={kycStatus.card?.kyc_level?.color || 'gray'}>
                                                <div className="kyc-header">
                                                    <div className="kyc-title">
                                                        <div className="icon-box">
                                                            <FaCreditCard />
                                                        </div>
                                                        <h4>{t('kyc.cardLabel')}</h4>
                                                    </div>
                                                    <span className="kyc-badge">
                                                        {kycStatus.card?.is_approved ? (
                                                            <><FaCheckCircle /> {kycStatus.card?.kyc_level?.label}</>
                                                        ) : (
                                                            <><FaClock /> {kycStatus.card?.kyc_level?.label}</>
                                                        )}
                                                    </span>
                                                </div>
                                                <p className="kyc-description">{kycStatus.card?.kyc_level?.description}</p>
                                            </KycStatusItem>
                                        </KycStatusGrid>
                                    ) : (
                                        <EmptyKycState>
                                            {t('kyc.clickToSync')}
                                        </EmptyKycState>
                                    )}
                                </div>

                                {/* PIX Online */}
                                <PaymentMethodItem $enabled={tunaPaymentMethods.pix}>
                                    <div className="method-info">
                                        <div className="icon-box">
                                            <MdQrCode2 />
                                        </div>
                                        <div className="details">
                                            <h3>{t('methods.pixOnline.title')}</h3>
                                            <p>{t('methods.pixOnline.description')}</p>
                                        </div>
                                    </div>
                                    <div className="toggle-container">
                                        {savingMethod === 'tuna_pix' && (
                                            <div className="saving-indicator">
                                                <FaSpinner /> {t('loading.saving')}
                                            </div>
                                        )}
                                        <Toggle
                                            $enabled={tunaPaymentMethods.pix}
                                            onClick={() => handleToggleTuna('pix')}
                                            disabled={savingMethod === 'tuna_pix'}
                                        />
                                    </div>
                                </PaymentMethodItem>

                                {/* Cartao Online */}
                                <PaymentMethodItem
                                    $enabled={tunaPaymentMethods.credit_card}
                                    $disabled={!kycStatus?.card?.can_enable}
                                >
                                    <div className="method-info">
                                        <div className="icon-box">
                                            <FaCreditCard />
                                        </div>
                                        <div className="details">
                                            <h3>
                                                {t('methods.creditCardOnline.title')}
                                                {!kycStatus?.card?.can_enable && (
                                                    <span className="badge warning">
                                                        <FaClock /> {t('status.awaitingKyc')}
                                                    </span>
                                                )}
                                            </h3>
                                            <p>{t('methods.creditCardOnline.description')}</p>
                                        </div>
                                    </div>
                                    <div className="toggle-container">
                                        {savingMethod === 'tuna_card' && (
                                            <div className="saving-indicator">
                                                <FaSpinner /> {t('loading.saving')}
                                            </div>
                                        )}
                                        <Toggle
                                            $enabled={tunaPaymentMethods.credit_card}
                                            onClick={() => handleToggleTuna('credit_card')}
                                            disabled={savingMethod === 'tuna_card' || !kycStatus?.card?.can_enable}
                                            title={!kycStatus?.card?.can_enable ? t('status.awaitingKyc') : ''}
                                        />
                                    </div>
                                </PaymentMethodItem>

                                {/* Aviso KYC Cartao */}
                                {kycStatus && !kycStatus.card?.can_enable && (
                                    <WarningBox>
                                        <div className="warning-content">
                                            <FaExclamationTriangle />
                                            <div className="text">
                                                <h4>{t('warnings.cardNotAvailable.title')}</h4>
                                                <p>{t('warnings.cardNotAvailable.description')}</p>
                                            </div>
                                        </div>
                                    </WarningBox>
                                )}
                            </>
                        )}
                    </SectionCard>
                    </FeatureGate>
                </ContentWrapper>
            </PageContainer>
        </PermissionGate>
    );
};

export default FormasPagamento;
